import BigNumber from 'bignumber.js'
import { BigNumber as EthersBigNumber } from '@ethersproject/bignumber'
import poolsConfig from 'config/constants/pools'
import sousChefABI from 'config/abi/sousChef.json'
import nftStakingAbi from 'config/abi/nftStakingPool.json'
import autoCompoundAbi from 'config/abi/ACPHodl.json'
import erc20ABI from 'config/abi/erc20.json'
import erc721CollectionABI from 'config/abi/erc721collection.json'
import hodlHandSaleABI from 'config/abi/hodlHandSale.json'
import multicall, { multicallv2, multicallWithOtherApi } from 'utils/multicall'
import { getAddress } from 'utils/addressHelpers'
import { BIG_TEN, BIG_ZERO } from 'utils/bigNumber'
import chunk from 'lodash/chunk'
import sousChefV2 from '../../config/abi/sousChefV2.json'
import sousChefV3 from '../../config/abi/sousChefV3.json'
import { useErc721CollectionContract } from 'hooks/useContract'
import { useBNBBusdPrice } from 'hooks/useBUSDPrice'

const poolsACP = poolsConfig.filter((p) => p.isAutoCompounding)
const poolsWithEnd = poolsConfig.filter((p) => p.isBuyNFT !== true && p.isAutoCompounding !== true)

const startEndBlockCalls = poolsWithEnd.flatMap((poolConfig) => {
  return [
    {
      address: getAddress(poolConfig.contractAddress),
      name: 'startBlock',
    },
    {
      address: getAddress(poolConfig.contractAddress),
      name: 'bonusEndBlock',
    },
  ]
})

const startEndTimeCalls = poolsACP.flatMap((poolConfig) => {
  return [
    {
      address: getAddress(poolConfig.contractAddress),
      name: 'startTime',
    },
    {
      address: getAddress(poolConfig.contractAddress),
      name: 'endTime',
    },
  ]
})

const abisForBlockLimits = poolsWithEnd.flatMap((poolConfig) => {
  return [
    poolConfig.isNft ? nftStakingAbi : sousChefABI,
    poolConfig.isNft ? nftStakingAbi : sousChefABI
  ]
})

export const fetchPoolsBlockLimits = async () => {
  // const startEndBlockRaw = await multicall(sousChefABI, startEndBlockCalls)
  const startEndBlockRaw = await multicallWithOtherApi(abisForBlockLimits, startEndBlockCalls)

  const startEndBlockResult = startEndBlockRaw.reduce((resultArray, item, index) => {
    const chunkIndex = Math.floor(index / 2)

    if (!resultArray[chunkIndex]) {
      // eslint-disable-next-line no-param-reassign
      resultArray[chunkIndex] = [] // start a new chunk
    }

    resultArray[chunkIndex].push(item)

    return resultArray
  }, [])

  return poolsWithEnd.map((cakePoolConfig, index) => {
    const [[startBlock], [endBlock]] = startEndBlockResult[index]
    return {
      sousId: cakePoolConfig.sousId,
      startBlock: startBlock.toNumber(),
      endBlock: endBlock.toNumber(),
    }
  })
}

export const fetchPoolsTimeLimits = async () => {
  const startEndTimeRaw = await multicallWithOtherApi([autoCompoundAbi,autoCompoundAbi], startEndTimeCalls)

  const startEndTimesResult = startEndTimeRaw.reduce((resultArray, item, index) => {
    const chunkIndex = Math.floor(index / 2)

    if (!resultArray[chunkIndex]) {
      // eslint-disable-next-line no-param-reassign
      resultArray[chunkIndex] = [] // start a new chunk
    }

    resultArray[chunkIndex].push(item)

    return resultArray
  }, [])

  return poolsACP.map((cakePoolConfig, index) => {
    const [[startTime], [endTime]] = startEndTimesResult[index]
    return {
      sousId: cakePoolConfig.sousId,
      startTime: startTime.toNumber(),
      endTime: endTime.toNumber(),
    }
  })
}

const poolsBalanceOf = poolsConfig.map((poolConfig) => {
  return {
    address: poolConfig.stakingToken.address,
    name: 'balanceOf',
    params: [getAddress(poolConfig.contractAddress)],
  }
})

const abisForTotalStaking = poolsConfig.map((poolConfig) => {
  return poolConfig.isNft ? erc721CollectionABI : erc20ABI
})

export const fetchPoolsTotalStaking = async () => {
  // const poolsTotalStaked = await multicall(erc20ABI, poolsBalanceOf)
  const poolsTotalStaked = await multicallWithOtherApi(abisForTotalStaking, poolsBalanceOf)

  return poolsConfig.map((p, index) => ({
    sousId: p.sousId,
    totalStaked: new BigNumber(poolsTotalStaked[index]).toJSON(),
  }))
}

const nftPriceCall = poolsConfig.filter((poolConfig) => poolConfig.isNft || poolConfig.sousId === 0 || poolConfig.sousId === 15).map((poolConfig) => {
  return {
    address: (poolConfig.sousId !==0 && poolConfig.sousId !==15) ? poolConfig.stakingToken.address : poolConfig.contractAddress[56],
    name: (poolConfig.sousId !==0 && poolConfig.sousId !==15) ? 'getPrice' : 'getTokenAmountFromOneHand',
    params: []
  }
})

const nftDiscountCall = poolsConfig.filter((poolConfig) => poolConfig.sousId === 0 || poolConfig.sousId === 15).map((poolConfig) => {
  return {
    address: poolConfig.contractAddress[56],
    name: 'discountRate'
  }
})

const abisForNftPrice = poolsConfig.filter((poolConfig) => poolConfig.isNft || poolConfig.sousId === 0 || poolConfig.sousId === 15).map((poolConfig) => {
  return (poolConfig.sousId !==0 && poolConfig.sousId !==15) ? erc721CollectionABI : hodlHandSaleABI
})

const abisForDiscountRate = poolsConfig.filter((poolConfig) => poolConfig.sousId === 0 || poolConfig.sousId === 15).map((poolConfig) => {
  return hodlHandSaleABI
})


export const fetchNftPrice = async () => {
  const nftPrice = await multicallWithOtherApi(abisForNftPrice, nftPriceCall)
  return nftPrice.map((price, i) => ({
    address: nftPriceCall[i].address,
    price: i === 9 ? new BigNumber(price).div(BIG_TEN.pow(9)).toJSON() : new BigNumber(price).div(BIG_TEN.pow(18)).toJSON()
  }))
}

export const fetchDiscountRate = async () => {
  const discountRate = await multicallWithOtherApi(abisForDiscountRate, nftDiscountCall)
  return discountRate.map((rate, i) => ({
    address: nftDiscountCall[i].address,
    rate: Number(rate)
  }))
}

export const fetchPoolsStakingLimits = async (
  poolsWithStakingLimit: number[],
): Promise<{ [key: string]: { stakingLimit: BigNumber; numberBlocksForUserLimit: number } }> => {
  const validPools = poolsConfig
    .filter((p) => p.stakingToken.symbol !== 'BNB' && !p.isFinished && !p.isAutoCompounding)
    .filter((p) => !poolsWithStakingLimit.includes(p.sousId))

  // Get the staking limit for each valid pool
  const poolStakingCalls = validPools
    .map((validPool) => {
      const contractAddress = getAddress(validPool.contractAddress)
      return ['hasUserLimit', 'poolLimitPerUser', 'numberBlocksForUserLimit'].map((method) => ({
        address: contractAddress,
        name: method,
      }))
    })
    .flat()

  const poolStakingResultRaw = await multicallv2(sousChefV2, poolStakingCalls, { requireSuccess: false })
  const chunkSize = poolStakingCalls.length / validPools.length
  const poolStakingChunkedResultRaw = chunk(poolStakingResultRaw.flat(), chunkSize)
  return poolStakingChunkedResultRaw.reduce((accum, stakingLimitRaw, index) => {
    const hasUserLimit = stakingLimitRaw[0]
    const stakingLimit = hasUserLimit && stakingLimitRaw[1] ? new BigNumber(stakingLimitRaw[1].toString()) : BIG_ZERO
    const numberBlocksForUserLimit = stakingLimitRaw[2] ? (stakingLimitRaw[2] as EthersBigNumber).toNumber() : 0
    return {
      ...accum,
      [validPools[index].sousId]: { stakingLimit, numberBlocksForUserLimit },
    }
  }, {})
}

const poolsWithV3 = poolsConfig.filter((pool) => pool?.version === 3)

export const fetchPoolsProfileRequirement = async (): Promise<{
  [key: string]: {
    required: boolean
    thresholdPoints: BigNumber
  }
}> => {
  const poolProfileRequireCalls = poolsWithV3
    .map((validPool) => {
      const contractAddress = getAddress(validPool.contractAddress)
      return ['pancakeProfileIsRequested', 'pancakeProfileThresholdPoints'].map((method) => ({
        address: contractAddress,
        name: method,
      }))
    })
    .flat()

  const poolProfileRequireResultRaw = await multicallv2(sousChefV3, poolProfileRequireCalls, { requireSuccess: false })
  const chunkSize = poolProfileRequireCalls.length / poolsWithV3.length
  const poolStakingChunkedResultRaw = chunk(poolProfileRequireResultRaw.flat(), chunkSize)
  return poolStakingChunkedResultRaw.reduce((accum, poolProfileRequireRaw, index) => {
    const hasProfileRequired = poolProfileRequireRaw[0]
    const profileThresholdPoints = poolProfileRequireRaw[1]
      ? new BigNumber(poolProfileRequireRaw[1].toString())
      : BIG_ZERO
    return {
      ...accum,
      [poolsWithV3[index].sousId]: {
        required: hasProfileRequired,
        thresholdPoints: profileThresholdPoints.toJSON(),
      },
    }
  }, {})
}
